// DEV-ONLY operator switcher — a clearly dev-styled fixed-corner control to hop
// between the seeded operator personas. Renders NOTHING when dev-auth is
// inactive, so the whole subtree tree-shakes out of a production build.
//
// This matters more here than on the customer console: every ops route carries
// its OWN frozen role list (billing is admin-only, licenses revenue-only, audit
// admin+auditor+exec), so "does this surface work for this persona" is only
// answerable by BEING that persona. The left rail is gated on exactly these
// roles — switching personas is how that gating gets checked.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  fetchOpsDevUsers,
  getSelectedOpsDevUserKey,
  isOperatorDevAuthActive,
  selectOpsDevUser,
  type OpsDevUserSummary,
} from "@/auth/dev-auth";

export function DevOperatorSwitcher(): React.ReactElement | null {
  const { t } = useTranslation();
  const [users, setUsers] = useState<readonly OpsDevUserSummary[]>([]);
  const [selected, setSelected] = useState<string>(() => getSelectedOpsDevUserKey());

  useEffect(() => {
    if (!isOperatorDevAuthActive()) return;
    let cancelled = false;
    void fetchOpsDevUsers().then((rows) => {
      if (!cancelled) setUsers(rows);
    });
    return (): void => {
      cancelled = true;
    };
  }, []);

  if (!isOperatorDevAuthActive()) return null;

  return (
    <aside aria-label={t("auth.dev.switcher_label")} className="fixed bottom-3 right-3 z-50 flex items-center gap-2 rounded-md border border-dashed border-[var(--color-border)] bg-[var(--color-card)] px-2 py-1 text-xs shadow-md">
      <span className="rounded bg-[var(--color-accent)] px-1.5 py-0.5 font-mono font-semibold text-[var(--color-accent-foreground)]">
        {t("auth.dev.badge")}
      </span>
      <select
        aria-label={t("auth.dev.switcher_label")}
        value={selected}
        onChange={(e): void => {
          setSelected(e.target.value);
          selectOpsDevUser(e.target.value);
        }}
        className="rounded border border-[var(--color-border)] bg-[var(--color-background)] px-1 py-0.5 text-xs text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
      >
        {users.length === 0 ? (
          <option value={selected}>{t("auth.dev.loading")}</option>
        ) : (
          users.map((u) => (
            <option key={u.key} value={u.key}>
              {u.name}
            </option>
          ))
        )}
      </select>
    </aside>
  );
}
